import { useMemo } from 'react';
import { DateTime } from 'luxon';
import type { Locales } from '@/i18n/i18n-types';
import {
  bucketStartIso,
  compareIsoDateStrings,
  formatChartAxisLabel,
  type ChartGranularity,
} from '@/lib/chart-axis';

export type ChartPointWithDate = { date: string };

function resolveBucketKey(
  dateIso: string,
  granularity: ChartGranularity,
  locale: Locales
): string | null {
  return (
    bucketStartIso(dateIso, granularity, locale) ??
    (DateTime.fromISO(dateIso).isValid ? dateIso : null)
  );
}

/**
 * Rows carry `date`: the ORIGINAL api date of the first point that fell in the
 * bucket (never the key regrouped in the browser zone), so click handlers can
 * send the exact backend bucket start (AD-011 / R1).
 */
export function bucketSumChartData<T extends ChartPointWithDate>(
  points: T[],
  granularity: ChartGranularity,
  locale: Locales,
  getValue: (point: T) => number,
  valueKey: string
): Array<{ month: string } & Record<string, number | string>> {
  const merged = new Map<string, { date: string; value: number }>();
  for (const point of points) {
    const k = resolveBucketKey(point.date, granularity, locale);
    if (!k) continue;
    const current = merged.get(k);
    merged.set(k, {
      date: current?.date ?? point.date,
      value: (current?.value ?? 0) + getValue(point),
    });
  }
  return [...merged.entries()]
    .sort(([a], [b]) => compareIsoDateStrings(a, b))
    .map(([iso, { date, value }]) => ({
      month: formatChartAxisLabel(DateTime.fromISO(iso), granularity, locale),
      date,
      [valueKey]: value,
    }));
}

export function bucketMergeChartData<T extends ChartPointWithDate, R>(
  points: T[],
  granularity: ChartGranularity,
  locale: Locales,
  merge: (point: T, current: R | undefined) => R,
  toRow: (iso: string, record: R) => Record<string, number | string>
): Array<{ month: string } & Record<string, number | string>> {
  const merged = new Map<string, { date: string; record: R }>();
  for (const point of points) {
    const k = resolveBucketKey(point.date, granularity, locale);
    if (!k) continue;
    const current = merged.get(k);
    merged.set(k, {
      date: current?.date ?? point.date,
      record: merge(point, current?.record),
    });
  }
  return [...merged.entries()]
    .sort(([a], [b]) => compareIsoDateStrings(a, b))
    .map(([iso, { date, record }]) => ({
      month: formatChartAxisLabel(DateTime.fromISO(iso), granularity, locale),
      date,
      ...toRow(iso, record),
    }));
}

export function mapPointsToChartData<T extends ChartPointWithDate, R>(
  points: T[],
  granularity: ChartGranularity,
  locale: Locales,
  mapPoint: (point: T) => R | null
): Array<{ month: string } & R> {
  return points.flatMap((point) => {
    const dt = DateTime.fromISO(point.date);
    if (!dt.isValid) return [];
    const mapped = mapPoint(point);
    if (mapped === null) return [];
    return [
      {
        month: formatChartAxisLabel(dt, granularity, locale),
        ...mapped,
      },
    ];
  });
}

export function useBucketedSumChartData<T extends ChartPointWithDate>(
  points: T[],
  granularity: ChartGranularity,
  locale: Locales,
  getValue: (point: T) => number,
  valueKey: string
): Array<{ month: string } & Record<string, number | string>> {
  return useMemo(
    () => bucketSumChartData(points, granularity, locale, getValue, valueKey),
    // getValue is intentionally omitted — callers should inline stable extractors
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points, granularity, locale, valueKey]
  );
}

export function useBucketedMergeChartData<T extends ChartPointWithDate, R>(
  points: T[],
  granularity: ChartGranularity,
  locale: Locales,
  merge: (point: T, current: R | undefined) => R,
  toRow: (iso: string, record: R) => Record<string, number | string>
): Array<{ month: string } & Record<string, number | string>> {
  return useMemo(
    () => bucketMergeChartData(points, granularity, locale, merge, toRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points, granularity, locale]
  );
}

export function useMappedChartData<T extends ChartPointWithDate, R>(
  points: T[],
  granularity: ChartGranularity,
  locale: Locales,
  mapPoint: (point: T) => R | null
): Array<{ month: string } & R> {
  return useMemo(
    () => mapPointsToChartData(points, granularity, locale, mapPoint),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [points, granularity, locale]
  );
}
